'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FullNameStep, CredentialsStep, UniqueLinkStep } from './components';
import { StepsFooter } from './components/steps/StepsFooter';
import { useRouter, useSearchParams } from 'next/navigation';
import { RoleStep } from './components/steps/RoleStep';
import { HeardFromStep } from './components/steps/HeardFromStep';

const steps = [
  { step: 1, key: 'username', component: UniqueLinkStep },
  { step: 2, key: 'name', component: FullNameStep },
  { step: 3, key: 'role', component: RoleStep },
  { step: 4, key: 'credentials', component: CredentialsStep },
  {
    step: 5,
    key: 'confirmation',
    component: HeardFromStep,
  },
] as const;

export const MultiStepForm = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [currentStep, setCurrentStep] = useState(() => {
    const sp = Number(searchParams.get('step'));
    return Number.isFinite(sp) && sp >= 1 && sp <= steps.length ? sp : 1;
  });
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    const sp = Number(searchParams.get('step'));
    if (Number.isFinite(sp) && sp >= 1 && sp <= steps.length) {
      setCurrentStep(sp);
    }
    initializedRef.current = true;
  }, [searchParams]);

  useEffect(() => {
    if (!initializedRef.current) return;
    const urlStep = searchParams.get('step');
    const nextStr = String(currentStep);
    if (urlStep !== nextStr) {
      const sp = new URLSearchParams(Array.from(searchParams.entries()));
      sp.set('step', nextStr);
      router.replace(`?${sp.toString()}`, { scroll: false });
    }
  }, [currentStep, searchParams, router]);

  const goNext = () => setCurrentStep((p) => Math.min(p + 1, steps.length));
  const goBack = () => setCurrentStep((p) => Math.max(p - 1, 1));
  const goToStep = (s: number) => s >= 1 && s <= steps.length && setCurrentStep(s);

  const StepComponent =
    useMemo(() => steps.find((s) => s.step === currentStep)?.component ?? (() => null), [currentStep]);
    return (
      <div className="relative flex flex-col flex-1 justify-between">
        <StepComponent onNext={goNext} />
        <StepsFooter
          onBack={goBack}
          step={currentStep}
          totalSteps={steps.length}
          goToStep={goToStep}
        />
      </div>
    );
  };